import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Icon,
  InputAdornment,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useDebounce } from "../../../../hooks/useDebounce";
import { stockMovementLogsService } from "../../services/stock-movement-logs.service";
import {
  StockMovementLogEntry,
  StockMovementLogsFilters,
  StockMovementLogsResponse,
} from "../../types/stock-movement-logs.types";
import { AppTable } from "../../../../components/AppTable";
import { TableColumn, TableToolbarActions } from "../../../../app.interface";

const THEME_COLORS = {
  primary: "#0891B2",
  success: "#059669",
  error: "#DC2626",
  warning: "#F59E0B",
  background: {
    light: "#F8FAFC",
    card: "#FFFFFF",
  },
  border: {
    light: alpha("#0891B2", 0.1),
    main: alpha("#0891B2", 0.2),
  },
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
  },
};

interface StockMovementLogsState {
  movements: StockMovementLogEntry[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: {
    total_in_quantity: number;
    total_out_quantity: number;
    total_value_in: number;
    total_value_out: number;
    net_quantity: number;
    net_value: number;
  } | null;
}

const StockMovementLogs: React.FC = () => {
  const theme = useTheme();

  // State management
  const [state, setState] = useState<StockMovementLogsState>({
    movements: [],
    loading: false,
    error: null,
    total: 0,
    page: 1, // Changed to 1-based pagination for AppTable compatibility
    pageSize: 20,
    totalPages: 0,
    summary: null,
  });

  // Filters state
  const [filters, setFilters] = useState<StockMovementLogsFilters>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(30, "day")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [calculateBalances, setCalculateBalances] = useState(false);
  const [sortField, setSortField] = useState("movement_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Debounced search
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Load stock movement logs
  const loadMovements = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const requestFilters: StockMovementLogsFilters = {
        ...filters,
        search: debouncedSearchTerm || undefined,
        date_range:
          startDate && endDate
            ? {
                start: startDate.valueOf(),
                end: endDate.valueOf(),
              }
            : undefined,
      };

      const response = await stockMovementLogsService.getStockMovementLogs({
        page: state.page, // Already 1-based
        page_size: state.pageSize,
        filters: requestFilters,
        sort_field: sortField,
        sort_order: sortOrder,
        calculate_balances: calculateBalances,
      });

      if (response.data.success) {
        const data = response.data.data;
        setState((prev) => ({
          ...prev,
          movements: data.movements,
          total: data.total,
          totalPages: data.total_pages,
          summary: data.summary,
          loading: false,
        }));
      } else {
        throw new Error(response.data.message || "Failed to load movements");
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to load stock movements",
      }));
      toast.error("Failed to load stock movements");
    }
  }, [
    filters,
    debouncedSearchTerm,
    startDate,
    endDate,
    state.page,
    state.pageSize,
    sortField,
    sortOrder,
    calculateBalances,
  ]);

  // Effects
  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  // Event handlers
  const handleFilterChange = (
    field: keyof StockMovementLogsFilters,
    value: any
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
    setState((prev) => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (page: number) => {
    setState((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setState((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(direction);
    setState((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm("");
    setStartDate(dayjs().subtract(30, "day"));
    setEndDate(dayjs());
    setState((prev) => ({ ...prev, page: 1 }));
  };

  // Utility function for currency formatting
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 3,
    }).format(amount);
  };

  // Column definitions for AppTable
  const getColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      {
        id: "movement_date",
        header: "Date & Time",
        field: "movement_date",
        type: "datetime",
        dateFormat: "custom",
        customDateFormat: "MMM d, yyyy HH:mm:ss",
        sortable: true,
      },
      {
        id: "movement_id",
        header: "Movement ID",
        field: "movement_id",
        type: "text",
        width: "w-40",
      },
      {
        id: "movement_type",
        header: "Movement Type",
        field: "display_movement_type",
        type: "status",
        statusConfig: {
          "grn in": { label: "GRN IN", color: "green" },
          "transfer in": { label: "TRANSFER IN", color: "blue" },
          "adjustment in": { label: "ADJUSTMENT IN", color: "blue" },
          "production in": { label: "PRODUCTION IN", color: "green" },
          "sale out": { label: "SALE OUT", color: "rose" },
          "transfer out": { label: "TRANSFER OUT", color: "yellow" },
          "adjustment out": { label: "ADJUSTMENT OUT", color: "yellow" },
          "wastage out": { label: "WASTAGE OUT", color: "rose" },
        },
        accessor: (row) =>
          row.display_movement_type?.toLowerCase() || "unknown",
      },
      {
        id: "product_id",
        header: "Item ID",
        field: "product_id",
        type: "text",
        width: "w-32",
      },
      {
        id: "item_name",
        header: "Item Name",
        field: "item_name",
        type: "text",
        sortable: true,
        width: "min-w-48",
      },
      {
        id: "warehouse_location",
        header: "Warehouse/Location",
        field: "warehouse_location",
        type: "text",
        accessor: (row) => row.warehouse_location || "-",
      },
      {
        id: "unit_cost",
        header: "Unit Cost",
        field: "unit_cost",
        type: "money",
        align: "right",
        sortable: true,
      },
      {
        id: "source_document_number",
        header: "Reference",
        field: "source_document_number",
        type: "text",
        accessor: (row) => row.source_document_number || "-",
      },
      {
        id: "in_quantity",
        header: "In Quantity",
        field: "in_quantity",
        type: "text",
        align: "right",
        accessor: (row) =>
          row.in_quantity > 0
            ? `${row.in_quantity.toLocaleString()} ${row.unit}`
            : "-",
      },
      {
        id: "out_quantity",
        header: "Out Quantity",
        field: "out_quantity",
        type: "text",
        align: "right",
        accessor: (row) =>
          row.out_quantity > 0
            ? `${row.out_quantity.toLocaleString()} ${row.unit}`
            : "-",
      },
      {
        id: "total_value_in",
        header: "Total Value In",
        field: "total_value_in",
        type: "money",
        align: "right",
        accessor: (row) => (row.total_value_in > 0 ? row.total_value_in : 0),
      },
      {
        id: "total_value_out",
        header: "Total Value Out",
        field: "total_value_out",
        type: "money",
        align: "right",
        accessor: (row) => (row.total_value_out > 0 ? row.total_value_out : 0),
      },
    ];

    // Add balance columns if calculateBalances is true
    if (calculateBalances) {
      baseColumns.push(
        {
          id: "balance_quantity",
          header: "Balance Quantity",
          field: "balance_quantity",
          type: "text",
          align: "right",
          accessor: (row) =>
            `${row.balance_quantity?.toLocaleString() || 0} ${row.unit}`,
        },
        {
          id: "balance_value",
          header: "Balance Value",
          field: "balance_value",
          type: "money",
          align: "right",
          accessor: (row) => row.balance_value || 0,
        }
      );
    }

    return baseColumns;
  };

  // Toolbar actions for AppTable
  const toolbarActions: TableToolbarActions = {
    refresh: {
      label: "Refresh Data",
      is_disabled: state.loading,
      onClick: loadMovements,
    },
    export: {
      label: "Export Data",
      is_disabled: state.loading || state.movements.length === 0,
      options: {
        formats: ["csv", "excel"],
        filename: "stock-movement-logs",
      },
      datasource: "pos_stock_movement",
      filters: [
        {
          id: "date_range",
          title: "Date Range",
          field: "movement_date",
          operator: "between",
          value:
            startDate && endDate
              ? [startDate.valueOf(), endDate.valueOf()]
              : undefined,
        },
        ...(filters.movement_type
          ? [
              {
                id: "movement_type",
                title: "Movement Type",
                field: "movement_type",
                operator: "equals",
                value: filters.movement_type,
              },
            ]
          : []),
        ...(filters.source_type
          ? [
              {
                id: "source_type",
                title: "Source Type",
                field: "source_type",
                operator: "equals",
                value: filters.source_type,
              },
            ]
          : []),
        ...(debouncedSearchTerm
          ? [
              {
                id: "search",
                title: "Search",
                field: "item_name",
                operator: "contains",
                value: debouncedSearchTerm,
              },
            ]
          : []),
      ],
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ color: THEME_COLORS.text.primary }}
          >
            Stock Movement Logs
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: THEME_COLORS.text.secondary }}
          >
            Comprehensive audit trail of all stock movements with detailed
            tracking
          </Typography>
        </Box>

        {/* Summary Cards */}
        {state.summary && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="success.main">
                    Total In
                  </Typography>
                  <Typography variant="h5">
                    {state.summary.total_in_quantity.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(state.summary.total_value_in)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="error.main">
                    Total Out
                  </Typography>
                  <Typography variant="h5">
                    {state.summary.total_out_quantity.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(state.summary.total_value_out)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary.main">
                    Net Quantity
                  </Typography>
                  <Typography variant="h5">
                    {state.summary.net_quantity.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quantity Balance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary.main">
                    Net Value
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(state.summary.net_value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Value Balance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            {/* Search */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Item name, movement ID, reference..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Date Range */}
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue as Dayjs | null)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue as Dayjs | null)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>

            {/* Movement Type */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Movement Type</InputLabel>
                <Select
                  value={filters.movement_type || ""}
                  label="Movement Type"
                  onChange={(e) =>
                    handleFilterChange("movement_type", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="IN">IN</MenuItem>
                  <MenuItem value="OUT">OUT</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Source Type */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Source Type</InputLabel>
                <Select
                  value={filters.source_type || ""}
                  label="Source Type"
                  onChange={(e) =>
                    handleFilterChange("source_type", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="GRN">GRN</MenuItem>
                  <MenuItem value="TRANSFER">Transfer</MenuItem>
                  <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                  <MenuItem value="PRODUCTION">Production</MenuItem>
                  <MenuItem value="SALE">Sale</MenuItem>
                  <MenuItem value="WASTAGE">Wastage</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearFilters}
                sx={{ height: "56px" }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>

          {/* Balance Calculation Toggle */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={calculateBalances}
                    onChange={(e) => setCalculateBalances(e.target.checked)}
                  />
                }
                label="Calculate running balances (may slow down the query)"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Error Display */}
        {state.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {state.error}
          </Alert>
        )}

        {/* AppTable */}
        <AppTable
          data={state.movements.map((movement) => ({
            ...movement,
            _id: movement.movement_id,
          }))}
          columns={getColumns()}
          loading={state.loading}
          sortConfig={{
            field: sortField,
            direction: sortOrder,
          }}
          onSort={handleSort}
          pagination={{
            total: state.total,
            page: state.page,
            pageSize: state.pageSize,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          toolbarActions={toolbarActions}
          styleOptions={{
            bordered: true,
            columnSeparators: false,
          }}
          emptyState={{
            icon: "inventory",
            title: "No Stock Movements Found",
            description:
              "There are no stock movements matching your current filters. Try adjusting your search criteria or date range.",
            actionLabel: "Clear Filters",
            onAction: clearFilters,
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default StockMovementLogs;
